import React, { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { MessageCircle, Bookmark, BarChart2, Calendar, User, Sparkles } from 'lucide-react';
import { useSavedItems } from '../contexts/SavedItemsContext';
import { useAuth } from '../contexts/AuthContext';
import RegisterPromptModal from './RegisterPromptModal';
import './Layout.css';
import useKeyboardInset from '../hooks/useKeyboardInset';

const NAV_ITEMS = [
  { to: '/', icon: MessageCircle, label: 'Chat', end: true, fillWhenActive: true },
  { to: '/saved', icon: Bookmark, label: 'Saved', badge: 'saved', requiresAuth: true,
    promptTitle: 'Save your favorites', promptMessage: 'Create a free account to save properties and come back to them anytime.' },
  { to: '/compare', icon: BarChart2, label: 'Compare', requiresAuth: true,
    promptTitle: 'Compare side by side', promptMessage: 'Create a free account to line up compounds and compare them in detail.' },
  { to: '/visits', icon: Calendar, label: 'Visits', requiresAuth: true,
    promptTitle: 'Book property visits', promptMessage: 'Create a free account to schedule and manage your property visits.' },
  { to: '/account', icon: User, label: 'Account', requiresAuth: true,
    promptTitle: 'Welcome to AI Broker', promptMessage: 'Sign up free to save properties, compare options, and track your visits — all in one place.' },
];

const Layout = ({ backendUrl, backendStatus, onRetryConnection }) => {
  const { savedKeys } = useSavedItems();
  const { isAuthenticated } = useAuth();
  const [authPrompt, setAuthPrompt] = useState(null); // { title, message } | null
  // The conversation is one conversation, wherever the user is standing. Held
  // here rather than in ChatPage so the compound and compare screens continue
  // it instead of starting a blank session — which is what discarded
  // everything the agent had learned the moment someone tapped "View details".
  const [sessionId, setSessionId] = useState(null);
  const savedCount = savedKeys?.size || 0;

  const badgeCount = (badge) => (badge === 'saved' ? savedCount : 0);

  // Guests tapping a gated tab get a dismissible prompt instead of a hard
  // redirect to the sign-in page, so they keep their current context.
  const handleNavClick = (item) => (e) => {
    if (item.requiresAuth && !isAuthenticated) {
      e.preventDefault();
      setAuthPrompt({
        title: item.promptTitle,
        message: item.promptMessage,
      });
    }
  };

  // Shrinks the shell by the height the on-screen keyboard covers, so the
  // composer and the newest messages stay visible on iOS. See the hook.
  useKeyboardInset();

  return (
    <div className="app-shell">
      {/* Desktop sidebar (>=1024px) */}
      <aside className="side-nav" aria-label="Primary">
        <div className="side-nav-brand">
          <span className="side-nav-logo-mark">
            <Sparkles size={18} color="#E8A020" />
          </span>
          <span className="side-nav-logo-text">AI Broker</span>
        </div>
        <div className="side-nav-items">
          {NAV_ITEMS.map((item) => {
            const { to, icon: Icon, label, end, badge } = item;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={handleNavClick(item)}
                className={({ isActive }) => `side-nav-item${isActive ? ' active' : ''}`}
              >
                <span className="side-nav-icon">
                  <Icon size={20} strokeWidth={1.9} />
                  {badgeCount(badge) > 0 && <span className="nav-badge">{badgeCount(badge)}</span>}
                </span>
                <span className="side-nav-label">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </aside>

      <main className="app-main">
        <Outlet context={{ backendUrl, backendStatus, onRetryConnection, sessionId, setSessionId }} />
      </main>

      {/* Mobile bottom nav (<1024px) */}
      <nav className="bottom-nav" aria-label="Primary">
        {NAV_ITEMS.map((item) => {
          const { to, icon: Icon, label, end, badge, fillWhenActive } = item;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={handleNavClick(item)}
              className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
            >
              {({ isActive }) => (
                <>
                  <span className="bottom-nav-icon">
                    <Icon
                      size={22}
                      strokeWidth={isActive ? 2.5 : 1.8}
                      fill={isActive && fillWhenActive ? 'currentColor' : 'none'}
                    />
                    {badgeCount(badge) > 0 && <span className="nav-badge">{badgeCount(badge)}</span>}
                  </span>
                  <span className="bottom-nav-label">{label}</span>
                  <span className="bottom-nav-dot" aria-hidden />
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <RegisterPromptModal
        open={!!authPrompt}
        onClose={() => setAuthPrompt(null)}
        title={authPrompt?.title}
        message={authPrompt?.message}
      />
    </div>
  );
};

export default Layout;
